import {
  AJS_ANONYMOUS_ID,
  CHIME_APPLICATION_TOKEN,
  CONVERSION_TRACKED,
  CSRF_TOKEN,
  REFERRAL_TOKEN,
  REFERRER_NAME,
} from 'consts/storageConsts';

import { APPLICATION_STATS_COOKIE } from 'hooks/useApplicationStats';
import { APP_STATS_TIME_ON_PAGE } from 'hooks/useCaptureTimeOnPage';
import { APP_STATS_KEYSTROKES } from 'hooks/useTrackKeystrokeNumber';

import isStringifiedJSONObject from 'utils/isStringifiedJSONObject';
import { getLogger } from 'utils/logger/getLogger';
import { getQueryParamFromUrl } from 'utils/queryParamHelpers';
import { deleteAllCookies, deleteCookies } from 'utils/storage/cookies';
import { getCookies } from 'utils/storage/getCookies';

const LOCAL_STORAGE_ERROR_MESSAGE = 'Could not access local storage';

export function clearLocalStorage() {
  try {
    localStorage.clear();
  } catch (err) {
    const { LOG_LEVELS, loggerHelper } = getLogger();
    loggerHelper({
      consoleOnly: true,
      level: LOG_LEVELS.WARNING,
      message: LOCAL_STORAGE_ERROR_MESSAGE,
      info: err,
    });
  }
}

export function removeFromLocalStorage(key) {
  try {
    localStorage.removeItem(key);
  } catch (err) {
    const { LOG_LEVELS, loggerHelper } = getLogger();
    loggerHelper({
      consoleOnly: true,
      level: LOG_LEVELS.WARNING,
      message: LOCAL_STORAGE_ERROR_MESSAGE,
      info: err,
    });
  }
}

export function setInLocalStorage(key, value) {
  try {
    localStorage.setItem(key, value);
  } catch (err) {
    const { LOG_LEVELS, loggerHelper } = getLogger();
    loggerHelper({
      consoleOnly: true,
      level: LOG_LEVELS.WARNING,
      message: LOCAL_STORAGE_ERROR_MESSAGE,
      info: err,
    });
  }
}

export function getFromLocalStorage(key) {
  let value = null;

  try {
    value = localStorage.getItem(key);
  } catch (err) {
    const { LOG_LEVELS, loggerHelper } = getLogger();
    loggerHelper({
      consoleOnly: true,
      level: LOG_LEVELS.WARNING,
      message: LOCAL_STORAGE_ERROR_MESSAGE,
      info: err,
    });
  }

  return value;
}

/**
 * Clear local data stores for the given key
 *
 * @param {string} key    Key of the data to be cleared
 */
export function deleteValueFromStorage(key) {
  try {
    removeFromLocalStorage(key);
    removeFromLocalStorage(`${key}-expiration`);
    const cookies = getCookies();
    cookies.remove(key);
  } catch (err) {
    const { LOG_LEVELS, loggerHelper } = getLogger();
    loggerHelper({
      consoleOnly: true,
      level: LOG_LEVELS.WARNING,
      message: LOCAL_STORAGE_ERROR_MESSAGE,
      info: err,
    });
  }
}

/**
 * Store the given data in localStorage and/or cookies
 *
 * @param {string} key    Key for the data store
 * @param {*} value       Value to store
 * @param {int} expirationMinutes   How many minutes the data should be kept alive. 0 if it shouldn't expire
 *
 * TODO: Add more tests around this function and file
 */
export function setValueInStorage(key, value, expirationMinutes) {
  let newValue = value;
  const newExpirationMinutes =
    expirationMinutes === 0 ? 0 : expirationMinutes || 15;
  const valueIsSimpleType =
    typeof value === 'boolean' ||
    typeof value === 'number' ||
    typeof value === 'string';

  if (!valueIsSimpleType) {
    newValue = JSON.stringify(value);
  }

  const expirationDate = new Date(
    new Date().getTime() + newExpirationMinutes * 60 * 1000
  );
  try {
    setInLocalStorage(key, newValue);

    if (newExpirationMinutes > 0) {
      setInLocalStorage(`${key}-expiration`, expirationDate.toUTCString());
    }
  } catch (err) {
    const { LOG_LEVELS, loggerHelper } = getLogger();
    loggerHelper({
      consoleOnly: true,
      level: LOG_LEVELS.WARNING,
      message: 'Could not store localStorage data',
      info: err,
    });
  }

  const cookies = getCookies();
  cookies.set(key, newValue, {
    expires: expirationDate,
  });
}

/**
 * In getValueFromStorage and other functions, we use dynamic imports via helper
 * functions (getCookies and getLogger) to break the following circular dependency chain:
 *
 * loggerHelper.js → datadog.js → appConsts.js → storageHelper.js → cookies.js → loggerHelper.js
 *
 * By dynamically importing these modules only when needed, we prevent the
 * circular references from causing initialization errors.
 */
export function getValueFromStorage(
  cookieKey,
  localStorageKey,
  queryStringParametersKey
) {
  if (!cookieKey && !localStorageKey && !queryStringParametersKey) return null;

  // Use the getCookies helper for consistent dynamic imports
  const cookies = getCookies();
  const cookieValue = cookies.get(cookieKey);

  if (cookieValue) {
    return cookieValue;
  }

  try {
    let localStorageValue =
      localStorage && localStorage.getItem(localStorageKey || cookieKey);

    if (localStorageValue && localStorageValue !== 'null') {
      if (isStringifiedJSONObject(localStorageValue)) {
        localStorageValue = JSON.parse(localStorageValue);
      }
      const localExpirationUTCString = localStorage.getItem(
        `${localStorageKey || cookieKey}-expiration`
      );

      if (localExpirationUTCString) {
        return new Date() > Date.parse(localExpirationUTCString)
          ? null
          : localStorageValue;
      }

      return localStorageValue;
    }
  } catch (err) {
    const { LOG_LEVELS, loggerHelper } = getLogger();
    loggerHelper({
      consoleOnly: true,
      level: LOG_LEVELS.WARNING,
      message: LOCAL_STORAGE_ERROR_MESSAGE,
      info: err,
    });

    return null;
  }

  const queryStringParametersValue = getQueryParamFromUrl(
    queryStringParametersKey || cookieKey
  );

  if (queryStringParametersValue) {
    return queryStringParametersValue;
  }

  return null;
}

/**
 * Sets the given key and value in the global app namespace. Does nothing
 * if the window object is not available
 * @param {*} key     Key of global value
 * @param {*} value   Value to be stored
 */
export function setGlobalValue(key, value) {
  if (!key) return;

  if (global.window) {
    global.window.Chime = global.window.Chime || {};
    global.window.Chime[key] = value;
  }
}

/**
 * Gets the value for the given key from the global app namespace. Returns
 * null if the window object is not available, otherwise returns the value
 * associated with the key.
 * @param {*} key     Key of global value
 */
export function getGlobalValue(key) {
  if (!key) return null;

  if (global.window && global.window.Chime) {
    return global.window.Chime[key];
  }

  return null;
}

export function clearLocalData(clearAnonymousId) {
  clearLocalStorage();
  const cookies = getCookies();
  cookies.remove(CSRF_TOKEN);
  cookies.remove(APPLICATION_STATS_COOKIE);
  cookies.remove(APP_STATS_TIME_ON_PAGE);
  cookies.remove(APP_STATS_KEYSTROKES);
  cookies.remove(CHIME_APPLICATION_TOKEN);

  if (clearAnonymousId) {
    deleteValueFromStorage(AJS_ANONYMOUS_ID);
  }

  deleteValueFromStorage(CONVERSION_TRACKED);
  deleteValueFromStorage(CHIME_APPLICATION_TOKEN);
}

/**
 * Deletes all data and cookies (for creating fresh new sessions)
 */
export function clearAllLocalDataAndCookies() {
  clearLocalStorage();
  deleteAllCookies();
  // Referral cookies must be deleted with the global domain option
  const globalDomain = window.location.hostname.replace(/.+?\./, '.');
  deleteCookies([REFERRAL_TOKEN, REFERRER_NAME, CHIME_APPLICATION_TOKEN], {
    domain: globalDomain,
  });
}
